import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import SelectTemplateCollectionItemTree from 'notebook/entry-templates/SelectTemplateCollectionItemTree/SelectTemplateCollectionItemTree';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';

export interface GenerateLLMReportArgs {
  newEntryName: string;
  templateId: string;
  folderId: string;
}

export interface GenerateLLMReportModalProps {
  onRequestSubmit: (format: GenerateLLMReportArgs) => void;
  onRequestClose: () => void;
}

export default function GenerateLLMReportModal(props: GenerateLLMReportModalProps): JSX.Element {
  const [folderId, setFolderId] = useState<string | null>(
    FolderDefaults.get(FolderDefaultsKey.NOTEBOOK) ?? null
  );
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [newEntryName, setNewEntryName] = useState<string>('');

  return (
    <Modal
      title="Generate report"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        FolderDefaults.set(FolderDefaultsKey.NOTEBOOK, folderId!);
        props.onRequestSubmit({newEntryName, templateId: templateId!, folderId: folderId!});
      }}
      buttons={[
        {
          text: 'Create',
          submit: true,
          disabled: templateId == null || !newEntryName || folderId == null,
        },
      ]}
    >
      <div className={css(styles.banner)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={
            BenchlingConfig.LLM_SHOW_HUMAN_REVIEW_DISCLAIMER
              ? 'This feature is powered by AI, and may make mistakes. A human may review inputs and outputs to improve the feature.'
              : 'This feature is powered by AI, and may make mistakes. Always verify the accuracy of AI-generated content.'
          }
          supportingInfo={
            <span>
              To generate the requested content, the AI system will analyze the template and entries you
              provide. Attachments and linked items are not yet supported. See the{' '}
              <a
                href="https://help.benchling.com/hc/en-us/articles/20903648151565-AI-Reporting-Beta"
                target="_blank"
                rel="noopener noreferrer"
              >
                help article
              </a>{' '}
              for more details. For more information on AI security and privacy, look{' '}
              <a
                href="https://help.benchling.com/hc/en-us/articles/20901323667853-Security-and-privacy-for-LLMs-and-AI-powered-features"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              .
            </span>
          }
          action={{actionType: InlineBannerActionType.ACCORDION}}
        />
      </div>
      <p>
        Choose a template to use as the basis for a report summarizing the selected entries. The template
        should include directives indicating the parts of the report to generate.
      </p>
      <p>
        Each directive in the template should be surrounded by double square brackets and on a single line.
        Template text not surrounded by double square brackets will not be modified during report generation.
      </p>
      <p>For example:</p>
      <div className={css(styles.exampleTemplate)}>
        <h2>Introduction</h2>
        <p>[[Write an introduction.]]</p>
        <h2>Methods</h2>
        <p>[[Summarize the procedures used to conduct the experiments.]]</p>
      </div>
      <p>
        Access your <a href={TemplateCollectionDAO.collectionsURL()}>template collections</a> to view, edit,
        and create new templates.
      </p>
      <FormGroup>
        <FieldLabel label="Template" isRequired={true} />
        <SelectTemplateCollectionItemTree
          collectibleType="TEMPLATE"
          collectibleId={templateId || ''}
          setCollectible={(template) => {
            if (template) {
              setTemplateId(template.id);
              setNewEntryName(template.name);
            } else {
              setTemplateId(null);
              setNewEntryName('');
            }
          }}
        />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Name" isRequired={true} />
        <TextInput
          value={newEntryName}
          onChange={(e) => {
            setNewEntryName(clampEntryName(e.target.value));
          }}
          autoComplete="off"
        />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Folder" isRequired={true} />
        <FolderSelector
          enableRedesign={true}
          showNotebook={true}
          value={folderId}
          onChange={(e) => {
            setFolderId(e.target.value);
          }}
          unsetOnFetchFail={true}
        />
      </FormGroup>
    </Modal>
  );
}

const styles = StyleSheet.create({
  banner: {
    marginBottom: 10,
  },
  exampleTemplate: {
    paddingBottom: 10,
    paddingLeft: 15,
    // Since, currently, it is semantically important for directives to be on one line, ensure that example
    // template lines do not wrap onto multiple lines.
    whiteSpace: 'nowrap',
    // Prevent these non-wrapping lines from occluding other content if they are wider than the modal, only
    // displaying a scroll bar if necessary.
    overflowX: 'scroll',
    overflow: 'auto',
  },
});
